import {PrintStoryDoc, PrintStoryEvent, PrintStorySeverity} from '../types'
import {toMillis} from '../time'

const mapBackendSeverity = (severity?: string): PrintStorySeverity => {
  switch (String(severity || '').toLowerCase()) {
    case 'normal':
      return 'success'
    case 'info':
      return 'info'
    case 'attention':
      return 'warning'
    case 'error':
    case 'connectivity':
      return 'danger'
    default:
      return 'warning'
  }
}

const isHeartbeatMissing = (type?: string) => String(type || '') === 'heartbeat_missing'

const isHeartbeatStatus = (type?: string) => String(type || '').startsWith('heartbeat_status_')

export const mapMishapToEvents = (mishap: PrintStoryDoc): PrintStoryEvent[] => {
  const events: PrintStoryEvent[] = []
  const startedAtMs = toMillis(mishap.startedAtMs) || toMillis(mishap.createdAt)
  if (!startedAtMs) {
    return events
  }

  const endedAtMs = mishap.resolved ? toMillis(mishap.endedAtMs) : null
  const type = String(mishap.type || '')
  const severity = mapBackendSeverity(mishap.severity)
  const deviceId = mishap.deviceId ? String(mishap.deviceId) : undefined
  const base = {
    lane: 'printer_health' as const,
    laneKey: deviceId || mishap.id,
    deviceId,
    jobId: mishap.jobId ? String(mishap.jobId) : undefined,
    contentId: mishap.contentId ? String(mishap.contentId) : undefined,
    codeId: mishap.codeId ? String(mishap.codeId) : undefined,
    sourceCollection: 'printerMishaps',
    raw: mishap,
    severity,
  }

  if (isHeartbeatMissing(type)) {
    events.push({
      ...base,
      ts: startedAtMs,
      endTs: endedAtMs,
      kind: 'heartbeat_missing_started',
      title: 'Heartbeat missing started',
      detail: mishap.staffMessage || mishap.reason,
    })

    if (endedAtMs) {
      events.push({
        ...base,
        ts: endedAtMs,
        kind: 'heartbeat_recovered',
        severity: 'success',
        title: 'Heartbeat recovered',
        detail: mishap.staffMessage || mishap.reason,
      })
    }
    return events
  }

  if (isHeartbeatStatus(type) || type === 'print_callback_missing') {
    const title =
      type === 'print_callback_missing'
        ? 'Print callback missing mishap'
        : `Status issue: ${mishap.status || mishap.normalizedStatus || type}`

    events.push({
      ...base,
      ts: startedAtMs,
      endTs: endedAtMs,
      kind: 'status_issue_opened',
      title,
      detail: mishap.staffMessage || mishap.reason || mishap.status,
    })

    if (endedAtMs) {
      events.push({
        ...base,
        ts: endedAtMs,
        kind: 'status_issue_resolved',
        severity: 'success',
        title: 'Status issue resolved',
        detail: mishap.staffMessage || mishap.reason,
      })
    }
  }

  return events
}

export const mapMishapsToEvents = (mishaps: PrintStoryDoc[]): PrintStoryEvent[] =>
  mishaps.flatMap(mapMishapToEvents)

export const getMishapInterval = (
  mishap: PrintStoryDoc,
  now = Date.now()
): {startMs: number; endMs: number; resolved: boolean; type: string} | null => {
  const startMs = toMillis(mishap.startedAtMs) || toMillis(mishap.createdAt)
  if (!startMs) {
    return null
  }

  const resolved = mishap.resolved === true
  const endMs = resolved ? toMillis(mishap.endedAtMs) || now : now

  return {
    startMs,
    endMs: Math.max(endMs, startMs),
    resolved,
    type: String(mishap.type || ''),
  }
}

export const isOfflineMishap = (mishap: PrintStoryDoc) =>
  String(mishap.type || '') === 'heartbeat_missing'

export const isStatusMishap = (mishap: PrintStoryDoc) => {
  const type = String(mishap.type || '')
  return type.startsWith('heartbeat_status_') || type === 'print_callback_missing'
}
